import {skipToken} from '@reduxjs/toolkit/query';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {useGetAccountingOperationQuery} from '@dms/api/sales';

const POLLING_INTERVAL_MS = 30000;
const TERMINAL_SUMMARY_STATES = ['completed', 'failed', 'canceled'];

interface UseAccountingOperationArgs {
  checkoutId: string;
  orderId: string;
  paymentId: string;
}

/**
 * Reads the state of an external accounting operation.
 *
 * The doorbell (`useAccountingDoorbell`) is best effort — a notification
 * failure is swallowed on the backend, and a lost one would leave the screen
 * frozen — so polling stays as the safety net. It is deliberately slow: the
 * doorbell is what makes the screen feel live, polling only guarantees eventual
 * convergence.
 *
 * A `summaryState` this frontend does not recognise counts as non-terminal.
 * The milestone vocabulary is owned by the external accounting system and grows;
 * guessing that an unknown value means "done" would stop polling on exactly the
 * states nobody has taught the frontend about yet.
 */
export const useAccountingOperation = (args: UseAccountingOperationArgs | typeof skipToken) => {
  const [isTerminal, setIsTerminal] = useState(false);

  const accountingOperation = useGetAccountingOperationQuery(args, {
    pollingInterval: isTerminal ? 0 : POLLING_INTERVAL_MS,
  });

  const summaryState = accountingOperation.data?.accountingProcess.summaryState;
  const nextIsTerminal = isNotNil(summaryState) && TERMINAL_SUMMARY_STATES.includes(summaryState);

  if (nextIsTerminal !== isTerminal) {
    setIsTerminal(nextIsTerminal);
  }

  return accountingOperation;
};
